import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CalendarClock, CheckCircle, AlertTriangle, Clock, Eye, RotateCcw, MessageSquare, ChevronLeft, ChevronRight, Users, Crown } from 'lucide-react';
import { toast } from 'sonner';

const VIEW_TABS = [
  { value: 'today', label: 'Today', icon: CalendarClock },
  { value: 'upcoming', label: 'Upcoming', icon: Clock },
  { value: 'overdue', label: 'Overdue', icon: AlertTriangle },
  { value: 'completed', label: 'Completed', icon: CheckCircle },
];

export default function FollowUpsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('today');
  const [followUps, setFollowUps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [counsellor, setCounsellor] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [rescheduleDialog, setRescheduleDialog] = useState({ open: false, id: null, leadId: null });
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [noteDialog, setNoteDialog] = useState({ open: false, leadId: null });
  const [noteContent, setNoteContent] = useState('');

  useEffect(() => {
    API.get('/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const fetchFollowUps = useCallback(() => {
    const params = { view, page, limit: 50 };
    if (counsellor) params.counsellor = counsellor;
    if (groupFilter) params.group_filter = groupFilter;
    API.get('/follow-ups', { params }).then(r => {
      setFollowUps(r.data.follow_ups);
      setTotal(r.data.total);
    }).catch(() => {});
  }, [view, page, counsellor, groupFilter]);

  useEffect(() => { fetchFollowUps(); }, [fetchFollowUps]);

  const markComplete = async (id) => {
    try {
      await API.put(`/follow-ups/${id}`, { status: 'completed' });
      toast.success('Marked complete');
      fetchFollowUps();
    } catch { toast.error('Failed'); }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate) return;
    try {
      await API.put(`/follow-ups/${rescheduleDialog.id}`, { follow_up_date: rescheduleDate, status: 'pending' });
      toast.success('Rescheduled');
      setRescheduleDialog({ open: false, id: null, leadId: null });
      fetchFollowUps();
    } catch { toast.error('Failed'); }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !noteDialog.leadId) return;
    try {
      await API.post(`/leads/${noteDialog.leadId}/notes`, { content: noteContent, note_type: 'follow_up_note' });
      toast.success('Note added');
      setNoteDialog({ open: false, leadId: null });
      setNoteContent('');
    } catch { toast.error('Failed'); }
  };

  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="p-6 space-y-4" data-testid="follow-ups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Follow Ups</h1>
          <p className="text-sm text-slate-500 mt-1">{total} follow up{total !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={groupFilter || "all"} onValueChange={v => { setGroupFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-44 h-9 text-xs" data-testid="filter-group"><SelectValue placeholder="All Leads" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Leads</SelectItem>
              <SelectItem value="leaders">Group Leaders</SelectItem>
              <SelectItem value="members">Group Members</SelectItem>
              <SelectItem value="group_only">Group Booking Only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={counsellor || "all"} onValueChange={v => { setCounsellor(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-44 h-9 text-xs"><SelectValue placeholder="All Counsellors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Counsellors</SelectItem>
              {users.filter(u => u.role === 'counsellor').map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={view} onValueChange={v => { setView(v); setPage(1); }}>
        <TabsList className="bg-white border border-slate-200 p-1">
          {VIEW_TABS.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="text-xs gap-1.5 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid={`tab-${t.value}`}>
              <t.icon size={13} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {VIEW_TABS.map(tab => (
          <TabsContent key={tab.value} value={tab.value} className="mt-4">
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Lead</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Group</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Counsellor</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Stage</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Next Action</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Remarks</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {followUps.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-12 text-sm text-slate-400">No follow ups found</TableCell></TableRow>
                  ) : followUps.map(f => (
                    <TableRow key={f.id} className="hover:bg-slate-50" data-testid={`followup-row-${f.id}`}>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{f.lead_name}</p>
                          <p className="text-xs text-slate-400 font-mono">{f.lead_uid}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{f.lead_phone}</TableCell>
                      <TableCell>
                        {f.group_role === 'leader' ? (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px] gap-1"><Crown size={10} /> Leader</Badge>
                        ) : f.group_role === 'member' ? (
                          <Badge className="bg-blue-100 text-blue-800 text-[10px] gap-1"><Users size={10} /> Member</Badge>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{f.assigned_to_name}</TableCell>
                      <TableCell className="text-sm text-slate-700">{f.follow_up_date}</TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{f.stage_name}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-[150px] truncate">{f.next_action || '-'}</TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">{f.remarks || '-'}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${f.status === 'completed' ? 'bg-green-100 text-green-800' : f.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{f.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {f.status === 'pending' && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => markComplete(f.id)} title="Complete" data-testid={`complete-${f.id}`}>
                              <CheckCircle size={14} className="text-green-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setRescheduleDialog({ open: true, id: f.id, leadId: f.lead_id }); setRescheduleDate(''); }} title="Reschedule">
                            <RotateCcw size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setNoteDialog({ open: true, leadId: f.lead_id })} title="Add Note">
                            <MessageSquare size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/leads/${f.lead_id}`)} title="Open Lead">
                            <Eye size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {pages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                  <p className="text-xs text-slate-500">Page {page} of {pages}</p>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 w-7 p-0"><ChevronLeft size={14} /></Button>
                    <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="h-7 w-7 p-0"><ChevronRight size={14} /></Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialog.open} onOpenChange={v => setRescheduleDialog({ open: v, id: rescheduleDialog.id, leadId: rescheduleDialog.leadId })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reschedule Follow Up</DialogTitle></DialogHeader>
          <Input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} />
          <Button onClick={handleReschedule} className="bg-slate-900 hover:bg-slate-800">Reschedule</Button>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={v => setNoteDialog({ open: v, leadId: noteDialog.leadId })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Note</DialogTitle></DialogHeader>
          <Textarea placeholder="Enter note..." value={noteContent} onChange={e => setNoteContent(e.target.value)} />
          <Button onClick={handleAddNote} className="bg-slate-900 hover:bg-slate-800">Save Note</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
